import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { usePersona } from '@/hooks/usePersona'
import { getErrorMessage } from '@/services/api/client'
import { feedApi } from '../../../api/feedApi'
import type { FeedFilters } from '../../../types'

// ─── Query keys ───────────────────────────────────────────────────────────────

export const feedKeys = {
  all: ['notification-feed'] as const,
  myFeed: (filters: Omit<FeedFilters, 'tab'>) =>
    [...feedKeys.all, 'my', filters] as const,
  tenantFeed: (filters: Omit<FeedFilters, 'tab'>) =>
    [...feedKeys.all, 'tenant', filters] as const,
  platformMessages: () => ['platform-messages'] as const,
}

// ─── My Feed ─────────────────────────────────────────────────────────────────

export function useMyFeed(filters: Omit<FeedFilters, 'tab'>) {
  return useQuery({
    queryKey: feedKeys.myFeed(filters),
    queryFn: () => feedApi.getMyFeed({ ...filters, tab: 'my_feed' }),
    staleTime: 30_000,
    refetchInterval: 30_000,
    select: (res) => res.data,
  })
}

// ─── Tenant Feed (operator+ only) ────────────────────────────────────────────

export function useTenantFeed(filters: Omit<FeedFilters, 'tab'>) {
  const { role } = usePersona()
  const canView =
    role === 'tenant_owner' ||
    role === 'tenant_admin' ||
    role === 'tenant_operator'

  return useQuery({
    queryKey: feedKeys.tenantFeed(filters),
    queryFn: () => feedApi.getTenantFeed({ ...filters, tab: 'tenant_events' }),
    enabled: canView,
    staleTime: 30_000,
    refetchInterval: 30_000,
    select: (res) => res.data,
  })
}

// ─── Platform Messages ────────────────────────────────────────────────────────

export function usePlatformMessages(opts?: { enabled?: boolean; poll?: boolean }) {
  return useQuery({
    queryKey: feedKeys.platformMessages(),
    queryFn: () => feedApi.listPlatformMessages(),
    enabled: opts?.enabled ?? true,
    staleTime: 60_000,
    // The bell polls so a broadcast published while the app is open turns up without a reload
    // (paused while the tab is hidden, which is TanStack's default).
    refetchInterval: opts?.poll ? 60_000 : false,
    select: (res) => res.data,
  })
}

// ─── Escalations (NOTIF.P2.2) ─────────────────────────────────────────────────

export function useEscalations(opts?: { enabled?: boolean }) {
  return useQuery({
    queryKey: ['notification-escalations'] as const,
    queryFn: () => feedApi.listEscalations(),
    // The escalations endpoint is operator+ on the backend; gate the query so
    // lower roles never fire a guaranteed-403 request.
    enabled: opts?.enabled ?? true,
    staleTime: 15_000,
    refetchInterval: 30_000,
    select: (res) => res.data,
  })
}

export function useAcknowledgeEscalation() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (eventId: string) => feedApi.acknowledgeEscalation(eventId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['notification-escalations'] })
    },
  })
}

// ─── Retry delivery ───────────────────────────────────────────────────────────

export function useRetryDelivery() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => feedApi.retryDelivery(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: feedKeys.all })
      toast.success('Retry queued')
    },
    onError: (err: unknown) => {
      // Was previously silent: the button just stopped spinning and
      // reappeared, with no indication of WHY (e.g. a 409 "not in a
      // retryable state" from RetryNotification's eligibility check).
      toast.error(getErrorMessage(err, 'Failed to retry delivery'))
    },
  })
}

// ─── Mark single platform message read ───────────────────────────────────────

export function useMarkPlatformMessageRead() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => feedApi.markPlatformMessageRead(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: feedKeys.platformMessages() })
    },
  })
}

// ─── Mark all platform messages read ─────────────────────────────────────────

export function useMarkAllPlatformMessagesRead() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => feedApi.markAllRead(),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: feedKeys.platformMessages() })
    },
  })
}
